import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PartyPopper,
  ArrowLeft,
  Loader2,
  Check,
  Globe,
  Target,
  Users,
  Zap,
  TrendingUp,
  Sparkles,
  Rocket
} from 'lucide-react';
import { BusinessProfile, OnboardingStatus } from '../../services/onboardingService';

interface CompletionStepProps {
  profile: BusinessProfile | null;
  status: OnboardingStatus | null;
  onComplete: () => void;
  onBack: () => void;
  isLoading: boolean;
}

const CompletionStep: React.FC<CompletionStepProps> = ({
  profile,
  status,
  onComplete,
  onBack,
  isLoading
}) => {
  const { t } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const summaryItems = [
    {
      icon: Globe,
      label: t('onboarding.complete.websiteAnalyzed', 'Website analyzed'),
      value: profile?.website_url || t('onboarding.complete.notConfigured', 'Not configured'),
      done: !!profile?.website_url
    },
    {
      icon: Target,
      label: t('onboarding.complete.goalsDefined', 'Goals defined'),
      value: profile?.marketing_goals?.length
        ? t('onboarding.complete.goalsCount', '{{count}} goals', { count: profile.marketing_goals.length })
        : t('onboarding.complete.notDefined', 'Not defined'),
      done: (profile?.marketing_goals?.length || 0) > 0
    },
    {
      icon: Users,
      label: t('onboarding.complete.accountsConnected', 'Accounts connected'),
      value: status?.connectedAccountsCount
        ? t('onboarding.complete.accountsCount', '{{count}} accounts', { count: status.connectedAccountsCount })
        : t('onboarding.complete.none', 'None'),
      done: (status?.connectedAccountsCount || 0) > 0
    },
    {
      icon: TrendingUp,
      label: t('onboarding.complete.competitorsDiscovered', 'Competitors discovered'),
      value: status?.competitorsDiscovered
        ? t('onboarding.complete.competitorsCount', '{{count}} competitors', { count: status.competitorsDiscovered })
        : t('onboarding.complete.waiting', 'Waiting'),
      done: (status?.competitorsDiscovered || 0) > 0
    }
  ];

  return (
    <div className="relative p-5 sm:p-12 overflow-hidden">
      {/* Confetti Effect */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                backgroundColor: ['#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#22c55e'][
                  Math.floor(Math.random() * 5)
                ]
              }}
            />
          ))}
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-10 relative z-10">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 mb-6 shadow-2xl shadow-green-500/30 animate-bounce-slow">
          <PartyPopper className="h-10 w-10 text-white" />
        </div>

        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
          {t('onboarding.complete.title', 'All set!')}
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-md mx-auto">
          {t('onboarding.complete.subtitle', 'Your marketing platform is configured and ready to work for you')}
        </p>
      </div>

      {/* Summary */}
      <div className="max-w-lg mx-auto mb-10">
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-600">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
            {t('onboarding.complete.configSummary', 'Configuration summary')}
          </h3>

          <div className="space-y-4">
            {summaryItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex items-center justify-between py-2"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      item.done
                        ? 'bg-green-100 dark:bg-green-900/30'
                        : 'bg-gray-100 dark:bg-gray-600'
                    }`}>
                      <Icon className={`h-4 w-4 ${
                        item.done
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-400'
                      }`} />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">
                      {item.label}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm ${
                      item.done
                        ? 'text-gray-600 dark:text-gray-400'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}>
                      {item.value}
                    </span>
                    {item.done && (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* What's Next */}
      <div className="max-w-lg mx-auto mb-10">
        <h3 className="text-center text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
          {t('onboarding.complete.whatsNext', 'What happens now')}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="text-center p-4 rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-800 flex items-center justify-center mx-auto mb-2">
              <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {t('onboarding.complete.aiGenerating', 'AI generating content')}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('onboarding.complete.aiGeneratingDesc', 'Posts will be created automatically')}
            </div>
          </div>

          <div className="text-center p-4 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800">
            <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center mx-auto mb-2">
              <Zap className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {t('onboarding.complete.competitorAnalysis', 'Competitor analysis')}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('onboarding.complete.competitorAnalysisDesc', 'Monitoring in progress')}
            </div>
          </div>

          <div className="text-center p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800">
            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center mx-auto mb-2">
              <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {t('onboarding.complete.dashboardActive', 'Dashboard active')}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('onboarding.complete.dashboardActiveDesc', 'Real-time metrics')}
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center">
        <button
          onClick={onComplete}
          disabled={isLoading}
          className="group inline-flex items-center justify-center px-10 py-5 text-lg font-bold text-white bg-gradient-to-r from-primary-600 via-purple-600 to-pink-600 rounded-2xl shadow-2xl shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-6 w-6 mr-3 animate-spin" />
              {t('onboarding.complete.finishing', 'Finishing...')}
            </>
          ) : (
            <>
              <Rocket className="h-6 w-6 mr-3 group-hover:animate-pulse" />
              {t('onboarding.complete.goToDashboard', 'Go to Dashboard')}
            </>
          )}
        </button>
      </div>

      {/* Back button */}
      <div className="flex justify-center mt-6">
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t('onboarding.complete.backAndReview', 'Back and review')}
        </button>
      </div>

      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(-10px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          width: 10px;
          height: 10px;
          border-radius: 2px;
          animation: confetti 3s ease-out forwards;
        }
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default CompletionStep;
